import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const SearchResultCard = ({ 
  result, 
  searchQuery, 
  onSelect, 
  isSelected, 
  onNavigate 
}) => {
  const [showPreview, setShowPreview] = useState(false);

  const highlightText = (text, query) => {
    if (!query || !text) return text;
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text?.split(regex);
    
    return parts?.map((part, index) => 
      regex?.test(part) ? (
        <mark key={index} className="bg-yellow-200 text-yellow-900 px-1 rounded">
          {part}
        </mark>
      ) : part
    );
  };

  const getDocumentIcon = (type) => {
    const iconMap = {
      'pdf': 'FileText',
      'doc': 'FileText',
      'docx': 'FileText',
      'xls': 'Sheet',
      'xlsx': 'Sheet',
      'ppt': 'Presentation',
      'pptx': 'Presentation',
      'txt': 'File',
      'jpg': 'Image',
      'png': 'Image',
      'gif': 'Image'
    };
    return iconMap?.[type] || 'File';
  };

  const getRelevanceColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 70) return 'text-warning';
    return 'text-muted-foreground';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-6 hover:shadow-md transition-all duration-200 ${
      isSelected ? 'ring-2 ring-primary' : ''
    }`}>
      {/* Header */}
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
            <Icon 
              name={getDocumentIcon(result?.fileType)} 
              size={24} 
              className="text-muted-foreground" 
            />
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-card-foreground mb-1 truncate">
                {highlightText(result?.title, searchQuery)}
              </h3>
              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                <span className="flex items-center gap-1">
                  <Icon name="Folder" size={14} />
                  {result?.documentBase}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="User" size={14} />
                  {result?.author}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="Calendar" size={14} />
                  {formatDate(result?.lastModified)}
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <div className={`text-sm font-medium ${getRelevanceColor(result?.relevanceScore)}`}>
                {result?.relevanceScore}% match
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onSelect(result?.id)}
                iconName={isSelected ? "CheckSquare" : "Square"}
                iconSize={18}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Content Preview */}
      <div className="mb-4">
        <p className="text-sm text-muted-foreground leading-relaxed">
          {highlightText(result?.snippet, searchQuery)}
        </p>
      </div>
      {/* Thumbnail Preview */}
      {result?.thumbnail && (
        <div className="mb-4">
          <Image
            src={result?.thumbnail}
            alt={`Thumbnail preview of ${result?.title} document showing first page content`}
            className="w-full h-32 object-cover rounded-lg border border-border"
          />
        </div>
      )}
      {/* Metadata */}
      <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
        <div className="flex items-center gap-4">
          <span>Size: {formatFileSize(result?.fileSize)}</span>
          <span>Type: {result?.fileType?.toUpperCase()}</span>
          {result?.version && <span>v{result?.version}</span>}
        </div>
        {result?.tags && result?.tags?.length > 0 && (
          <div className="flex items-center gap-1">
            <Icon name="Tag" size={12} />
            <span>{result?.tags?.slice(0, 2)?.join(', ')}</span>
            {result?.tags?.length > 2 && <span>+{result?.tags?.length - 2}</span>}
          </div>
        )}
      </div>
      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => onNavigate('view', result)}
            iconName="Eye"
            iconSize={16}
          >
            View
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onNavigate('edit', result)}
            iconName="Edit"
            iconSize={16}
          >
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
            iconName={showPreview ? "ChevronUp" : "ChevronDown"}
            iconSize={16}
          >
            {showPreview ? 'Less' : 'Preview'}
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onNavigate('analyze', result)}
            iconName="Brain"
            iconSize={16}
          >
            Analyze
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onNavigate('download', result)}
            iconName="Download"
            iconSize={16}
          >
            Download
          </Button>
        </div>
      </div>
      {/* Extended Preview */}
      {showPreview && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="bg-muted rounded-lg p-4">
            <h4 className="text-sm font-medium text-card-foreground mb-2">Document Preview</h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {result?.fullPreview || result?.snippet}
            </p>
            {result?.keyPoints && result?.keyPoints?.length > 0 && (
              <div className="mt-3">
                <h5 className="text-xs font-medium text-card-foreground mb-2">Key Points:</h5>
                <ul className="text-xs text-muted-foreground space-y-1">
                  {result?.keyPoints?.map((point, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Icon name="ChevronRight" size={12} className="mt-0.5 flex-shrink-0" />
                      {highlightText(point, searchQuery)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchResultCard;